import { useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, downloadFile } from "../api/client";
import type { Guide, GuideDiff, GuidePassage, GuideSegment } from "../api/types";
import { GeneralNotesList } from "./GeneralNotes";
import { Icon } from "./icons";
import { toast } from "./toast";
import { backdropClose } from "../lib/backdrop";

/**
 * The Re-recording guide — the Pickups Package regrouped into the passages to
 * record again, one row per passage: where it begins (page, file, timecode),
 * the manuscript to read with every difference marked, what the current take
 * says, a Play button for exactly that passage, and a "Re-recorded" tick.
 *
 * Shared by the public link (`/share/:token/guide`) and the owner's in-app view:
 * `source.base` is the API root for either, and `source.anonymous` says whether
 * a tick has to ask who's ticking (share links have no account).
 *
 * The one rule that shapes the colours: the left-hand side is READ ALOUD by a
 * narrator, so nothing on it may look like "don't read this". No strikethrough
 * anywhere; words left out of the take are red to read, words read differently
 * amber, and where words were added is a thin green bar, never a character.
 */

export interface GuideSource {
  base: string;          // "/api/share/<token>" or "/api/modules/proofing/projects/<id>"
  queryKey: unknown[];
  anonymous: boolean;
}

const NAME_KEY = "mb.guide.name";

function readName(): string {
  try {
    return localStorage.getItem(NAME_KEY) || "";
  } catch {
    return "";
  }
}
/** Asked already — including "tick without a name", which is an answer too. */
function answered(): boolean {
  try {
    return localStorage.getItem(NAME_KEY) !== null;
  } catch {
    return true; // no storage: don't ask on every single tick
  }
}
function saveName(v: string) {
  try {
    localStorage.setItem(NAME_KEY, v);
  } catch {
    /* storage unavailable: the name just isn't remembered */
  }
}

export function diffText(d: GuideDiff): string {
  const s = (x: string) => (x || "").trim().replace(/^["“”]+|["“”]+$/g, "");
  if (d.kind === "added") return `added “${s(d.heard)}”`;
  if (d.kind === "missing") return `missing “${s(d.script)}”`;
  if (d.kind === "note") return `${(d.label || "Note").toLowerCase()}${s(d.script) ? ` at “${s(d.script)}”` : ""}`;
  return `“${s(d.script)}” → “${s(d.heard)}”`;
}

export const audioUrl = (base: string, chapterId: string) => base.replace("{chapter_id}", encodeURIComponent(chapterId));

const plain = (segs: GuideSegment[], side: "ms" | "rec") =>
  segs.map(([t, k]) => (side === "ms" && k === "ins" ? "" : t)).join("").replace(/\n\n/g, "\n");

const mmss = (s: number) => `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}`;

function fmtWhen(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso.endsWith("Z") || iso.includes("+") ? iso : iso + "Z");
  if (isNaN(d.getTime())) return "";
  return `${d.toLocaleDateString(undefined, { day: "numeric", month: "short" })} ${d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}`;
}

// --------------------------------------------------------------------------- //
// One shared <audio> for the page                                             //
// --------------------------------------------------------------------------- //
// Play seeks to 0.3 s before the passage and stops 0.4 s after it. The element
// streams the file's own range-capable URL, which redirects to a presigned one
// that expires after an hour: on an error, the source is asked for afresh once.
// Only the active button re-renders while it plays (a store, not list state).

type PlayState = { key: string; playing: boolean; frac: number; dur: number } | null;

class GuidePlayer {
  audio: HTMLAudioElement;
  state: PlayState = null;
  private cur: { key: string; a: number; b: number; chapter: string; retried: boolean } | null = null;
  private srcChapter = "";
  private raf = 0;
  private listeners = new Set<() => void>();

  constructor(private base: () => string, private onFail: (msg: string) => void) {
    this.audio = new Audio();
    this.audio.preload = "none";
    this.audio.addEventListener("ended", () => this.finish());
    this.audio.addEventListener("error", () => this.recover());
  }

  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };
  private emit() {
    this.listeners.forEach((f) => f());
  }
  private set(s: PlayState) {
    this.state = s;
    this.emit();
  }

  toggle(p: GuidePassage) {
    const audio = this.audio;
    if (this.cur && this.cur.key === p.key) {
      if (audio.paused) {
        audio.play().then(() => this.loop()).catch(() => {});
      } else {
        audio.pause();
        cancelAnimationFrame(this.raf);
        this.progress(false);
      }
      return;
    }
    this.finish();
    const a = Math.max(0, p.start - 0.3);
    const b = p.end + 0.4;
    this.cur = { key: p.key, a, b, chapter: p.chapter_id, retried: false };
    this.set({ key: p.key, playing: true, frac: 0, dur: b - a });
    this.start(false);
  }

  private start(fresh: boolean) {
    const audio = this.audio;
    const mine = this.cur;
    if (!mine) return;
    if (fresh || this.srcChapter !== mine.chapter) {
      audio.src = audioUrl(this.base(), mine.chapter) + (fresh ? `?r=${Date.now()}` : "");
      this.srcChapter = mine.chapter;
    }
    // Start inside the click (iOS keeps the permission to play), muted until
    // the seek lands on the passage.
    audio.muted = true;
    audio.addEventListener("seeked", () => {
      if (this.cur !== mine) return;
      audio.muted = false;
      this.loop();
    }, { once: true });
    const seek = () => {
      if (this.cur === mine) audio.currentTime = mine.a;
    };
    if (audio.readyState >= 1) seek();
    else audio.addEventListener("loadedmetadata", seek, { once: true });
    audio.play().catch(() => {
      if (this.cur !== mine) return;
      this.finish();
      this.onFail("That passage didn’t play. Try again, or use Full take.");
    });
  }

  private recover() {
    if (!this.cur) return;
    if (!this.cur.retried) {
      // Most often the presigned link behind the file expired: ask again.
      this.cur.retried = true;
      this.start(true);
      return;
    }
    this.finish();
    this.onFail("The audio didn’t load. Check your connection and try again.");
  }

  private progress(playing: boolean) {
    if (!this.cur) return;
    const t = this.audio.currentTime;
    const dur = this.cur.b - this.cur.a;
    this.set({ key: this.cur.key, playing, frac: Math.max(0, Math.min(1, (t - this.cur.a) / dur)), dur });
  }

  private loop = () => {
    if (!this.cur) return;
    if (this.audio.currentTime >= this.cur.b || this.audio.ended) {
      this.finish();
      return;
    }
    this.progress(!this.audio.paused);
    if (!this.audio.paused) this.raf = requestAnimationFrame(this.loop);
  };

  finish() {
    this.audio.pause();
    cancelAnimationFrame(this.raf);
    this.cur = null;
    if (this.state) this.set(null);
  }
}

function PlayButton({ player, p }: { player: GuidePlayer; p: GuidePassage }) {
  const snap = useSyncExternalStore(player.subscribe, () => {
    const s = player.state;
    return s && s.key === p.key ? `${s.playing ? 1 : 0}|${s.frac.toFixed(3)}|${s.dur}` : "";
  });
  const [on, frac, dur] = snap ? [snap.split("|")[0] === "1", Number(snap.split("|")[1]), Number(snap.split("|")[2])] : [false, 0, 0];
  const left = dur * (1 - frac);
  return (
    <button
      type="button"
      className={`rrg-play${on ? " on" : ""}`}
      style={{ ["--pct" as string]: `${(frac * 100).toFixed(1)}%` }}
      aria-pressed={on}
      aria-label={`Play the current take of passage ${p.id}`}
      onClick={() => player.toggle(p)}
    >
      <Icon name={on ? "pause" : "play"} size={14} />
      <span className="rrg-play-l">{snap ? `${Math.floor(left / 60)}:${String(Math.floor(left % 60)).padStart(2, "0")}` : "Play"}</span>
    </button>
  );
}

// --------------------------------------------------------------------------- //
// Text                                                                        //
// --------------------------------------------------------------------------- //

function Segs({ segs, side }: { segs: GuideSegment[]; side: "ms" | "rec" }) {
  return (
    <>
      {segs.map(([t, k], i) => {
        const text = t.replace(/\n\n/g, "\n");
        if (k === "sub") return <mark key={i} className="rrg-sub">{text}</mark>;
        if (k === "del") return <mark key={i} className="rrg-del" title="Left out of the take: record it">{text}</mark>;
        if (k === "note") return <mark key={i} className="rrg-note" title="A note on these words (noise, a defect…)">{text}</mark>;
        if (k === "ins") {
          return side === "ms"
            ? <span key={i} className="rrg-ins-pt" role="img" aria-label="words were added here in the take" title="Words were added here in the take" />
            : <ins key={i} className="rrg-ins">{text}</ins>;
        }
        return <span key={i}>{text}</span>;
      })}
    </>
  );
}

// --------------------------------------------------------------------------- //
// The page                                                                    //
// --------------------------------------------------------------------------- //

export function ReRecordingGuide({ source, pickupsHref, ownerBar, openTakeHref }: {
  source: GuideSource;
  pickupsHref?: string;
  ownerBar?: (g: Guide) => ReactNode;
  // Where "Full take" goes for a passage (defaults to the file itself, ~3 s before it)
  openTakeHref?: (p: GuidePassage, audioBase: string) => string;
}) {
  const qc = useQueryClient();
  const key = source.queryKey;
  const { data, isLoading, isError, error } = useQuery({
    queryKey: key,
    queryFn: () => api<Guide>(`${source.base}/guide`),
    refetchOnWindowFocus: true,
    refetchInterval: (q) => {
      const d = q.state.data as Guide | undefined;
      return d && (d.status === "building" || d.status === "updating") ? 3000 : 15000;
    },
  });

  const audioBase = useRef("");
  audioBase.current = data?.audio_base ?? "";
  const [player] = useState(() => new GuidePlayer(() => audioBase.current, (m) => toast(m, "error")));
  useEffect(() => () => player.finish(), [player]);

  const [q, setQ] = useState("");
  const [file, setFile] = useState("");
  const [status, setStatus] = useState<"all" | "todo" | "done">("all");
  const [asking, setAsking] = useState<{ p: GuidePassage } | null>(null);
  const [busy, setBusy] = useState<"" | "xlsx" | "pdf">("");

  const passages = data?.passages ?? [];
  const groups = useMemo(() => {
    const out: { file: string; chapter: string; items: GuidePassage[] }[] = [];
    const by = new Map<string, (typeof out)[number]>();
    for (const p of passages) {
      let g = by.get(p.audio_filename);
      if (!g) {
        g = { file: p.audio_filename, chapter: p.chapter, items: [] };
        by.set(p.audio_filename, g);
        out.push(g);
      }
      g.items.push(p);
    }
    return out;
  }, [passages]);
  const hay = useMemo(
    () => new Map(passages.map((p) => [p.key, [`#${p.id}`, `page ${p.page_start}`, `p${p.page_start}`, p.audio_filename,
      p.chapter, plain(p.manuscript_segments, "ms"), plain(p.recorded_segments, "rec")].join(" ").toLowerCase()])),
    [passages],
  );
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const idOnly = /^#\d+$/.test(needle) ? Number(needle.slice(1)) : null;   // "#29" is that passage, not #290
    return new Set(passages.filter((p) =>
      (!file || p.audio_filename === file)
      && (!needle || (idOnly !== null ? p.id === idOnly : (hay.get(p.key) || "").includes(needle)))
      && (status === "all" || (status === "done" ? p.addressed : !p.addressed)),
    ).map((p) => p.key));
  }, [passages, q, file, status, hay]);

  const tickM = useMutation({
    mutationFn: (v: { key: string; addressed: boolean; by: string }) =>
      api<{ addressed_at: string | null; addressed_by: string | null }>(`${source.base}/guide/addressed`, {
        method: "POST", body: JSON.stringify(v),
      }),
    onMutate: (v) => {
      qc.setQueryData<Guide>(key, (cur) => cur && cur.passages ? {
        ...cur,
        done: (cur.done ?? 0) + (v.addressed ? 1 : -1),
        passages: cur.passages.map((p) => p.key === v.key
          ? { ...p, addressed: v.addressed, addressed_by: v.addressed ? (v.by || p.addressed_by) : null,
              addressed_at: v.addressed ? new Date().toISOString() : null }
          : p),
      } : cur);
    },
    onError: () => toast("That tick didn’t save. Please try again.", "error"),
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });

  function tick(p: GuidePassage) {
    if (!p.addressed && source.anonymous && !answered()) {
      setAsking({ p });
      return;
    }
    tickM.mutate({ key: p.key, addressed: !p.addressed, by: source.anonymous ? readName() : "" });
  }

  async function exportFile(kind: "xlsx" | "pdf") {
    setBusy(kind);
    try {
      await downloadFile(`${source.base}/guide/export/${kind}`);
    } catch (e: any) {
      toast(e?.message || "The download didn’t start. Please try again.", "error");
    } finally {
      setBusy("");
    }
  }

  function exportCsv() {
    const qv = (v: unknown) => '"' + String(v ?? "").replace(/"/g, '""') + '"';
    const head = ["#", "Page", "Chapter", "File", "Timecode", "Length (s)", "Manuscript (record this)", "Current take",
      "Differences", "Re-recorded", "By", "When"];
    const lines = [head.map(qv).join(",")];
    for (const p of passages) {
      if (!shown.has(p.key)) continue;
      lines.push([p.id, p.page_start, p.chapter, p.audio_filename, p.timecode, p.length,
        plain(p.manuscript_segments, "ms"), plain(p.recorded_segments, "rec"), p.diffs.map(diffText).join("; "),
        p.addressed ? "yes" : "", p.addressed_by ?? "", p.addressed_at ?? ""].map(qv).join(","));
    }
    const blob = new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${data?.project_name || "Re-recording guide"} - re-recording progress.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  if (isError) {
    const status404 = (error as { status?: number })?.status === 404;
    return (
      <div className="empty-state">
        <h3>{status404 ? "This guide isn’t available" : "The guide didn’t load"}</h3>
        <p>{status404 ? "The link may have been turned off, or the address is incorrect." : "Check your connection and reload the page."}</p>
      </div>
    );
  }
  if (isLoading || !data) return <div className="empty-state"><p>Loading…</p></div>;

  const n = passages.length;
  const done = passages.filter((p) => p.addressed).length;
  const totalSec = passages.reduce((a, p) => a + p.length, 0);
  const takeHref = (p: GuidePassage) =>
    openTakeHref ? openTakeHref(p, data.audio_base) : `${audioUrl(data.audio_base, p.chapter_id)}#t=${Math.max(0, p.start - 3).toFixed(2)}`;

  if (!data.passages) {
    return (
      <div className="rrg">
        {ownerBar?.(data)}
        <div className="rrg-building panel">
          {data.status === "failed" ? (
            <>
              <h3>The guide couldn’t be built</h3>
              <p>{data.error || "Something went wrong while reading the proofing. It will try again shortly."}</p>
            </>
          ) : (
            <>
              <div className="eyebrow">Re-recording guide</div>
              <h3>Building the guide for {data.project_name}…</h3>
              <p>Grouping the pickups into sentences and finding where each passage begins in the audio. This takes a minute or two the first time.</p>
              <div className="rrg-bar"><i style={{ width: `${Math.max(4, data.progress ?? 0)}%` }} /></div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="rrg">
      <section className="rrg-head">
        <div>
          <div className="eyebrow">Re-recording guide</div>
          <h1>{data.project_name}</h1>
          <div className="rrg-sub-line">
            <b>{n}</b> passage{n === 1 ? "" : "s"} to re-record · from <b>{data.pickup_count ?? 0}</b> pickups · <b>{groups.length}</b> file{groups.length === 1 ? "" : "s"} · about <b>{mmss(totalSec)}</b> of audio to replace
          </div>
        </div>
        <div className="rrg-actions">
          <button className="btn primary" type="button" onClick={() => exportFile("xlsx")} disabled={!!busy}>
            {busy === "xlsx" ? <span className="spinner sm" /> : <Icon name="download" size={16} />} Spreadsheet (.xlsx)
          </button>
          <button className="btn" type="button" onClick={() => exportFile("pdf")} disabled={!!busy}>
            {busy === "pdf" ? <span className="spinner sm" /> : <Icon name="file-text" size={16} />} Highlighted manuscript (.pdf)
          </button>
          {pickupsHref && <a className="rrg-toplink" href={pickupsHref}>Pickups Package</a>}
        </div>
      </section>

      {ownerBar?.(data)}

      {(data.unplaced?.length ?? 0) > 0 && (
        <div className="rrg-updating rrg-warn" role="note">
          {data.unplaced!.length} pickup{data.unplaced!.length === 1 ? "" : "s"} couldn’t be placed in a passage — usually because the file hasn’t been proofed yet. They’re still in the Pickups Package.
        </div>
      )}
      {data.status === "updating" && (
        <div className="rrg-updating" role="status"><span className="spinner sm" /> Updating with the latest pickups — the list below is the previous version until it’s done.</div>
      )}

      <section className="panel rrg-overview">
        <div className="rrg-progress">
          <div className="eyebrow">Re-recorded</div>
          <div className="rrg-num">{done} <span>of {n}</span></div>
          <div className="rrg-bar" role="progressbar" aria-label="Passages re-recorded" aria-valuenow={done} aria-valuemax={n}>
            <i style={{ width: n ? `${(100 * done / n).toFixed(1)}%` : 0 }} />
          </div>
          <div className="rrg-mode"><span className="dot" /> Shared progress: ticks here and in the Pickups Package are the same ticks</div>
        </div>
        <div className="rrg-key">
          <p><strong>Left:</strong> the official manuscript. Record all of it, including the highlighted words. <strong>Right:</strong> what the current take contains. Each passage is a full sentence, or a run of adjacent sentences that all changed. <strong>Play</strong> plays the current take of that passage.</p>
          <div className="rrg-chips">
            <span className="rrg-chip k-sub">read differently</span>
            <span className="rrg-chip k-del">left out of the take</span>
            <span className="rrg-chip k-ins">added in the take</span>
            <span className="rrg-chip k-caret"><span className="rrg-ins-pt" />where words were added</span>
            {passages.some((p) => p.diffs.some((d) => d.kind === "note")) && <span className="rrg-chip k-note">a note on the words</span>}
          </div>
        </div>
      </section>

      <GeneralNotesList notes={data.notes ?? []} />

      <div className="rrg-toolbar" role="search">
        <label className="rrg-search">
          <Icon name="search" size={16} />
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search text, page or passage #" aria-label="Search passages" />
        </label>
        <select value={file} onChange={(e) => setFile(e.target.value)} aria-label="Chapter">
          <option value="">All chapters</option>
          {groups.map((g) => <option key={g.file} value={g.file}>{g.chapter ? `${g.chapter} · ` : ""}{g.file} ({g.items.length})</option>)}
        </select>
        <div className="rrg-seg" role="radiogroup" aria-label="Status">
          {(["all", "todo", "done"] as const).map((s) => (
            <button key={s} type="button" role="radio" aria-checked={status === s} className={status === s ? "on" : ""} onClick={() => setStatus(s)}>
              {s === "all" ? "All" : s === "todo" ? "To do" : "Done"}
            </button>
          ))}
        </div>
        <button className="rrg-linkbtn" type="button" onClick={exportCsv}>Export this view (.csv)</button>
        <span className="rrg-count">{shown.size === n ? `${n} passages` : `Showing ${shown.size} of ${n}`}</span>
      </div>

      {shown.size === 0 && <div className="empty-state"><p>No passages match. Try clearing the search or switching status.</p></div>}

      {groups.map((g) => {
        const items = g.items.filter((p) => shown.has(p.key));
        if (!items.length) return null;
        const secs = g.items.reduce((a, p) => a + p.length, 0);
        return (
          <section key={g.file} className="rrg-group">
            <div className="rrg-ghead">
              {g.chapter ? <h2>{g.chapter}</h2> : null}
              <span className={g.chapter ? "mono rrg-file" : "mono rrg-file rrg-file-only"}>{g.file}</span>
              <span className="rrg-meta">pp. {g.items[0].page_start}–{g.items[g.items.length - 1].page_end} · {g.items.length} passage{g.items.length === 1 ? "" : "s"} · {mmss(secs)} of audio</span>
            </div>
            {items.map((p) => (
              <article key={p.key} id={`p${p.id}`} className={`rrg-row${p.addressed ? " done" : ""}`}>
                <div className="rrg-cell rrg-m">
                  <div className="rrg-pid"><a href={`#p${p.id}`} title="Link to this passage">#{p.id}</a></div>
                  <div className="rrg-facts">
                    <span>Page {p.page_start}</span>
                    <span className="mono rrg-tc" title={`Where the passage begins in ${p.audio_filename}`}>{p.timecode}</span>
                    <span>{p.length.toFixed(1)} s</span>
                  </div>
                  <PlayButton player={player} p={p} />
                  <a className="rrg-take" href={takeHref(p)} target="_blank" rel="noopener noreferrer" title={`Open ${p.audio_filename} a few seconds before this passage (new tab)`}>Full take ↗</a>
                </div>
                <div className="rrg-cell rrg-ms">
                  <div className="rrg-lbl">Manuscript: record this</div>
                  <div className="rrg-txt"><Segs segs={p.manuscript_segments} side="ms" /></div>
                  <div className="rrg-diffs">
                    {p.diffs.map((d, i) => <span key={i} className={d.kind}>{diffText(d)}</span>)}
                  </div>
                </div>
                <div className="rrg-cell rrg-rec">
                  <div className="rrg-lbl">Current take</div>
                  <div className="rrg-txt"><Segs segs={p.recorded_segments} side="rec" /></div>
                </div>
                <div className="rrg-cell rrg-d">
                  <button type="button" className="rrg-tick" aria-pressed={p.addressed} onClick={() => tick(p)}>
                    <span className="rrg-box"><Icon name="check" size={12} /></span>
                    <span>Re-recorded</span>
                  </button>
                  {p.addressed && (
                    <div className="rrg-who">{[p.addressed_by, fmtWhen(p.addressed_at)].filter(Boolean).join(" · ")}</div>
                  )}
                </div>
              </article>
            ))}
          </section>
        );
      })}

      <footer className="rrg-foot">
        <span>Built {data.built_at ? fmtWhen(data.built_at) : ""} from the Pickups Package: {data.pickup_count} pickups grouped into {n} passages. Starts are placed where the narrator begins speaking.</span>
        <span>The current take is what the transcription heard, so its punctuation is the transcription’s. When in doubt, play the take.</span>
      </footer>

      {asking && (
        <NamePrompt
          onCancel={() => setAsking(null)}
          onSave={(name) => {
            saveName(name);
            const p = asking.p;
            setAsking(null);
            tickM.mutate({ key: p.key, addressed: true, by: name });
          }}
        />
      )}
    </div>
  );
}

function NamePrompt({ onSave, onCancel }: { onSave: (name: string) => void; onCancel: () => void }) {
  const [v, setV] = useState("");
  return (
    <div className="modal-overlay" {...backdropClose(onCancel)}>
      <form className="modal-card rrg-name" role="dialog" aria-modal="true" aria-labelledby="rrg-name-h"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => { e.preventDefault(); onSave(v.trim()); }}>
        <h3 id="rrg-name-h">Who’s ticking?</h3>
        <p>Your name goes next to the passages you tick, so everyone working through the guide can see who re-recorded what. This browser remembers it.</p>
        <input className="input" autoFocus value={v} onChange={(e) => setV(e.target.value)} placeholder="Your name" maxLength={80} aria-label="Your name" />
        <div className="modal-actions">
          <button type="button" className="btn" onClick={() => onSave("")}>Tick without a name</button>
          <button type="submit" className="btn primary" disabled={!v.trim()}>Save and tick</button>
        </div>
      </form>
    </div>
  );
}
